import { computed, onBeforeUnmount, ref, watch, type Ref } from 'vue'
import type { IMessage } from '@/modules/chat-matrix-fast/types/IChat'
import {
  pinMatrixMessage,
  pinMatrixMessagePersonal,
  resolvePinnedMessages,
  sendPinNotice,
  subscribeMatrixPinnedEvents,
  unpinMatrixMessage,
} from '@/modules/chat-matrix-fast/services/matrixPinnedEventsService'

export type PinConfirmOptions = {
  /** Диалог: также закрепить у собеседника (room state). false = только для меня. */
  pinForPeer?: boolean
  /** Группа: отправить notice-уведомление в ленту. */
  notifyMembers?: boolean
  preview?: string
  actorName?: string
}

export function usePinnedMessages(
  roomId: Ref<string | null | undefined>,
  localMessages: Ref<IMessage[]>,
) {
  const pinnedEventIds = ref<string[]>([])
  const pinnedMessages = ref<IMessage[]>([])
  const isPinning = ref(false)

  let unsubscribe: (() => void) | null = null
  let resolveToken = 0

  const refreshPreviews = async (ids: string[], currentRoomId: string) => {
    const token = ++resolveToken
    const resolved = await resolvePinnedMessages(currentRoomId, ids, localMessages.value)
    if (token !== resolveToken) {
      return
    }

    pinnedMessages.value = resolved
  }

  const bindRoom = (nextRoomId: string | null | undefined) => {
    unsubscribe?.()
    unsubscribe = null
    pinnedEventIds.value = []
    pinnedMessages.value = []

    if (!nextRoomId) {
      return
    }

    unsubscribe = subscribeMatrixPinnedEvents(nextRoomId, ids => {
      pinnedEventIds.value = ids
      void refreshPreviews(ids, nextRoomId)
    })
  }

  watch(roomId, bindRoom, { immediate: true })

  watch(localMessages, () => {
    const currentRoomId = roomId.value
    if (!currentRoomId || !pinnedEventIds.value.length) {
      return
    }

    void refreshPreviews(pinnedEventIds.value, currentRoomId)
  })

  onBeforeUnmount(() => {
    unsubscribe?.()
    unsubscribe = null
  })

  const pinnedCount = computed(() => pinnedEventIds.value.length)

  const isPinned = (eventId: string | null | undefined): boolean => {
    if (!eventId) {
      return false
    }

    return pinnedEventIds.value.includes(eventId)
  }

  const pinMessage = async (eventId: string, options: PinConfirmOptions = {}) => {
    const currentRoomId = roomId.value
    if (!currentRoomId || !eventId || isPinning.value) {
      return
    }

    isPinning.value = true
    try {
      const shared = options.pinForPeer !== false
      if (shared) {
        await pinMatrixMessage(currentRoomId, eventId)
      } else {
        await pinMatrixMessagePersonal(currentRoomId, eventId)
      }

      if (options.notifyMembers) {
        await sendPinNotice(currentRoomId, {
          eventId,
          preview: options.preview || '',
          actorName: options.actorName || 'Участник',
        })
      }
    } finally {
      isPinning.value = false
    }
  }

  const unpinMessage = async (
    eventId: string,
    scope: 'group' | 'dialog' = 'group',
  ) => {
    const currentRoomId = roomId.value
    if (!currentRoomId || !eventId || isPinning.value) {
      return
    }

    isPinning.value = true
    try {
      await unpinMatrixMessage(currentRoomId, eventId, { scope })
    } finally {
      isPinning.value = false
    }
  }

  return {
    pinnedEventIds,
    pinnedMessages,
    pinnedCount,
    isPinning,
    isPinned,
    pinMessage,
    unpinMessage,
  }
}
